import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Something to do while the app is getting ready.
 *
 * A wait needs no explanation, and explaining it — how the hosting works, why
 * the first visit is slower — hands the visitor a problem that is not theirs.
 * A wait they spend playing something is simply shorter.
 *
 * Drawn on a canvas rather than built from elements: it is a game loop, and a
 * game loop that mutates React state sixty times a second is a re-render
 * storm. Everything that moves lives in a ref; React is told only about the
 * one thing it needs to act on — whether a round is in progress, which
 * decides whether the app may move on without interrupting anybody.
 */

/** The world is this tall in its own units; everything scales off that, so the
 *  game plays the same on a phone in portrait as on a wide monitor. */
const WORLD_H = 100;

const GRAVITY = 210;
const FLAP = -62;
const SPEED = 38;
const GAP = 30;
const PIPE_W = 13;
const PIPE_SPACING = 58;
const BIRD_R = 3.2;
const GROUND_H = 7;
const MAX_TILT = 1.1;

/** Fixed physics step, so the game runs at one speed on any refresh rate. */
const STEP = 1 / 120;

const BEST_KEY = "rhythm.flap.best";

type Pipe = { x: number; gapY: number; passed: boolean };
type Phase = "ready" | "playing" | "dead";

type World = {
  phase: Phase;
  y: number;
  vy: number;
  pipes: Pipe[];
  score: number;
  best: number;
  t: number;
  width: number;
};

function readBest(): number {
  try {
    return Number(localStorage.getItem(BEST_KEY)) || 0;
  } catch {
    // Private browsing, or storage the browser refuses to hand over. A high
    // score is not worth a crash.
    return 0;
  }
}

function writeBest(value: number) {
  try {
    localStorage.setItem(BEST_KEY, String(value));
  } catch {
    /* nothing worth doing about it */
  }
}

export function WaitingGame({ onBusyChange }: { onBusyChange?: (busy: boolean) => void }) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const worldRef = useRef<World>({
    phase: "ready",
    y: WORLD_H / 2,
    vy: 0,
    pipes: [],
    score: 0,
    best: readBest(),
    t: 0,
    width: WORLD_H,
  });

  // The one thing React is told, and it is told once: somebody is playing.
  //
  // Deliberately not "a round is in progress" — that would clear itself the
  // instant a dead player tapped to try again, and the app, ready by then,
  // would take the screen away in the same gesture that started their next
  // round. Once you have played, this holds until you say you are done.
  const [engaged, setEngaged] = useState(false);

  const busyRef = useRef(onBusyChange);
  busyRef.current = onBusyChange;

  useEffect(() => {
    busyRef.current?.(engaged);
  }, [engaged]);

  const reset = useCallback((width: number) => {
    const world = worldRef.current;
    world.phase = "ready";
    world.y = WORLD_H / 2;
    world.vy = 0;
    world.score = 0;
    world.t = 0;
    world.width = width;
    world.pipes = [];
  }, []);

  const flap = useCallback(() => {
    const world = worldRef.current;
    setEngaged(true);

    if (world.phase === "dead") {
      reset(world.width);
      return;
    }

    if (world.phase === "ready") {
      world.phase = "playing";
      world.pipes = [
        { x: world.width + 10, gapY: WORLD_H / 2, passed: false },
        { x: world.width + 10 + PIPE_SPACING, gapY: WORLD_H / 2, passed: false },
      ];
    }

    world.vy = FLAP;
  }, [reset]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext("2d");
    if (!canvas || !context) return;

    // The room's palette rather than colours of its own, so the wait looks
    // like part of the app instead of a widget that wandered in.
    const styles = getComputedStyle(canvas);
    const colour = (name: string, fallback: string) =>
      styles.getPropertyValue(name).trim() || fallback;

    const ink = colour("--text", "#e6e8ec");
    const dim = colour("--text-dim", "#8b929e");
    const edge = colour("--edge", "#2f343c");
    const accent = colour("--accent", "#7d8794");
    const sunken = colour("--bg-sunken", "#0f1114");
    const raised = colour("--bg-raised", "#1e2126");

    // Someone who asks for less motion gets a still scene until they choose
    // to start one. The game itself is motion they asked for by tapping.
    const calm = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;

    let css = { w: 0, h: 0 };
    let scale = 1;

    const measure = () => {
      const rect = canvas.getBoundingClientRect();
      if (!rect.width || !rect.height) return;

      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.round(rect.width * dpr);
      canvas.height = Math.round(rect.height * dpr);
      context.setTransform(dpr, 0, 0, dpr, 0, 0);

      css = { w: rect.width, h: rect.height };
      scale = rect.height / WORLD_H;

      // The world is as wide as the box makes it. Nothing is positioned in
      // absolute pixels, so a tall phone and a short landscape window get the
      // same game rather than a cropped one.
      const width = rect.width / scale;
      const world = worldRef.current;
      world.width = width;
      if (world.phase === "ready") world.pipes = [];
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(canvas);

    const px = (value: number) => value * scale;

    // Type is sized in world units like everything else, but never allowed
    // below what a person can actually read: in a short landscape window the
    // world is only a couple of hundred pixels tall, and a label scaled purely
    // off that comes out at nine pixels.
    const type = (units: number, floor: number, weight = 500) => {
      const size = Math.max(floor, px(units));
      context.font = `${weight} ${size}px ${styles.fontFamily || "sans-serif"}`;
    };

    const step = () => {
      const world = worldRef.current;
      if (world.phase !== "playing") return;

      world.vy += GRAVITY * STEP;
      world.y += world.vy * STEP;

      for (const pipe of world.pipes) pipe.x -= SPEED * STEP;

      // Recycle rather than allocate: the pipe that leaves on the left is the
      // one that comes back on the right.
      const last = world.pipes[world.pipes.length - 1];
      if (world.pipes[0] && world.pipes[0].x < -PIPE_W) {
        const recycled = world.pipes.shift()!;
        recycled.x = (last?.x ?? world.width) + PIPE_SPACING;
        recycled.gapY = GAP / 2 + 6 + Math.random() * (WORLD_H - GROUND_H - GAP - 12);
        recycled.passed = false;
        world.pipes.push(recycled);
      }

      const birdX = world.width * 0.3;
      const floor = WORLD_H - GROUND_H - BIRD_R;

      for (const pipe of world.pipes) {
        if (!pipe.passed && pipe.x + PIPE_W < birdX - BIRD_R) {
          pipe.passed = true;
          world.score += 1;
        }

        const withinColumn = birdX + BIRD_R > pipe.x && birdX - BIRD_R < pipe.x + PIPE_W;
        const throughGap =
          world.y - BIRD_R > pipe.gapY - GAP / 2 && world.y + BIRD_R < pipe.gapY + GAP / 2;

        if (withinColumn && !throughGap) die(world);
      }

      if (world.y > floor) {
        world.y = floor;
        die(world);
      }

      // The ceiling stops you rather than ending the round — clipping the top
      // on a flap that was only slightly too strong is a miserable way to lose.
      if (world.y < BIRD_R) {
        world.y = BIRD_R;
        world.vy = 0;
      }
    };

    const die = (world: World) => {
      if (world.phase !== "playing") return;
      world.phase = "dead";
      if (world.score > world.best) {
        world.best = world.score;
        writeBest(world.score);
      }
    };

    const roundedRect = (x: number, y: number, w: number, h: number, r: number) => {
      const radius = Math.min(r, w / 2, Math.abs(h) / 2);
      context.beginPath();
      context.roundRect(x, y, w, h, radius);
    };

    const draw = () => {
      const world = worldRef.current;
      const groundY = px(WORLD_H - GROUND_H);

      const sky = context.createLinearGradient(0, 0, 0, css.h);
      sky.addColorStop(0, raised);
      sky.addColorStop(1, sunken);
      context.fillStyle = sky;
      context.fillRect(0, 0, css.w, css.h);

      // Something to make the movement legible — without it the bird looks
      // stationary between pipes.
      context.fillStyle = edge;
      context.globalAlpha = 0.5;
      const idle = world.phase === "ready" && calm;
      for (let i = 0; i < 6; i++) {
        const spacing = css.w / 5;
        const drift = idle ? 0 : (world.t * SPEED * scale * 0.25) % spacing;
        const x = css.w - ((i * spacing + drift) % (css.w + spacing));
        const y = ((i * 37) % 60) * (css.h / 200) + css.h * 0.08;
        roundedRect(x, y, px(9), px(2.4), px(1.2));
        context.fill();
      }
      context.globalAlpha = 1;

      for (const pipe of world.pipes) {
        const x = px(pipe.x);
        const w = px(PIPE_W);
        const gapTop = px(pipe.gapY - GAP / 2);
        const gapBottom = px(pipe.gapY + GAP / 2);

        context.fillStyle = edge;
        context.strokeStyle = accent;
        context.lineWidth = Math.max(1, px(0.4));

        roundedRect(x, -px(4), w, gapTop + px(4), px(1.6));
        context.fill();
        context.stroke();

        roundedRect(x, gapBottom, w, groundY - gapBottom, px(1.6));
        context.fill();
        context.stroke();
      }

      context.fillStyle = sunken;
      context.fillRect(0, groundY, css.w, css.h - groundY);
      context.strokeStyle = edge;
      context.lineWidth = Math.max(1, px(0.5));
      context.beginPath();
      context.moveTo(0, groundY);
      context.lineTo(css.w, groundY);
      context.stroke();

      const birdX = px(world.width * 0.3);
      const bob = world.phase === "ready" && !calm ? Math.sin(world.t * 3) * px(1.6) : 0;
      const birdY = px(world.y) + bob;
      const tilt =
        world.phase === "ready"
          ? 0
          : Math.max(-0.5, Math.min(MAX_TILT, world.vy / 90));

      context.save();
      context.translate(birdX, birdY);
      context.rotate(tilt);
      context.fillStyle = ink;
      context.beginPath();
      context.ellipse(0, 0, px(BIRD_R), px(BIRD_R * 0.85), 0, 0, Math.PI * 2);
      context.fill();
      context.fillStyle = accent;
      context.beginPath();
      context.ellipse(-px(0.6), px(0.4), px(1.5), px(1), -0.4, 0, Math.PI * 2);
      context.fill();
      context.fillStyle = sunken;
      context.beginPath();
      context.arc(px(1.2), -px(0.9), px(0.62), 0, Math.PI * 2);
      context.fill();
      context.restore();

      context.textAlign = "center";
      context.fillStyle = ink;

      // Only while flying: the round's final score is the headline of the
      // game-over card, and showing both reads as a mistake.
      if (world.phase === "playing") {
        type(9, 22, 600);
        const y = Math.max(px(16), 34);
        // Outlined, because the score sits where pipes pass behind it.
        context.strokeStyle = sunken;
        context.lineWidth = Math.max(3, px(1.4));
        context.lineJoin = "round";
        context.strokeText(String(world.score), css.w / 2, y);
        context.fillText(String(world.score), css.w / 2, y);
      }

      if (world.phase === "ready") {
        type(5.4, 16, 600);
        context.fillText("Tap to fly", css.w / 2, px(WORLD_H / 2) - Math.max(px(14), 26));
        context.fillStyle = dim;
        type(4, 12.5);
        context.fillText(
          world.best > 0 ? `Best ${world.best}` : "Space works too",
          css.w / 2,
          px(WORLD_H / 2) - Math.max(px(8), 10),
        );
      }

      if (world.phase === "dead") {
        context.fillStyle = sunken;
        context.globalAlpha = 0.72;
        context.fillRect(0, 0, css.w, css.h);
        context.globalAlpha = 1;

        context.fillStyle = ink;
        type(9, 28, 650);
        context.fillText(`${world.score}`, css.w / 2, px(WORLD_H / 2) - Math.max(px(4), 8));
        context.fillStyle = dim;
        type(4.2, 13);
        context.fillText(`Best ${world.best}`, css.w / 2, px(WORLD_H / 2) + Math.max(px(3), 12));
        context.fillText(
          "Tap to try again",
          css.w / 2,
          px(WORLD_H / 2) + Math.max(px(10), 32),
        );
      }
    };

    let frame = 0;
    let previous = performance.now();
    let carry = 0;

    const loop = (now: number) => {
      const elapsed = Math.min((now - previous) / 1000, 0.25);
      previous = now;
      carry += elapsed;
      worldRef.current.t += elapsed;

      // Capped: a tab that was in the background for a minute must not come
      // back and run a minute of physics in one frame.
      let steps = 0;
      while (carry >= STEP && steps < 8) {
        step();
        carry -= STEP;
        steps += 1;
      }
      if (carry > STEP * 8) carry = 0;

      draw();
      frame = requestAnimationFrame(loop);
    };

    frame = requestAnimationFrame(loop);

    const onKey = (e: KeyboardEvent) => {
      if (e.key === " " || e.key === "ArrowUp" || e.key === "Enter") {
        e.preventDefault();
        flap();
      }
    };

    window.addEventListener("keydown", onKey);

    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
      window.removeEventListener("keydown", onKey);
    };
  }, [flap]);

  return (
    <canvas
      ref={canvasRef}
      className="wait-game"
      onPointerDown={(e) => {
        e.preventDefault();
        flap();
      }}
      role="img"
      aria-label="A small game to pass the time. Tap, or press space, to fly."
    />
  );
}
